<template>
  <Card :disabled="!canRegister" bordered :radio="!user || user && selectedUser" :locked="tariff.isClosed && !canRegister" @input="selectTariff" @click="selectTariff" :value="tariff" :checked="value" :loading="isLoading">
    <h3>{{ tariff.title }}</h3>
    <p v-if="tariff.start !== tariff.end">{{ $t('course.period', { start: tariff.start, end: tariff.end }) }}</p>
    <p v-else-if="tariff.start === tariff.end">{{ tariff.start }}</p>
    <p class="text-warning" v-if="!isAlreadyRegistered && tariff.blockedRegistrationMessage && (!user || selectedUser) && !isLoading" v-html="tariff.blockedRegistrationMessage"></p>
    <Modal v-if="!isAlreadyRegistered && tariff.blockedRegistrationType === 'requiredCourses'" title="Verplichte cursussen" ref="requiredCoursesModal">
      <Table :headers="{ title: { label: 'Cursus of activiteit' }, tariffs: { label: 'Tarieven' }}" :records="tariff.blockedRegistrationData" noFooter noPagination>
        <template #data-tariffs="{ data }">
          {{ data.map(tariff => tariff.title).join(', ') }}
        </template>
      </Table>
    </Modal>
    <Alert v-if="hasOpenInvoice && parseInt(course.mollieOnly)" title="Openstaande kostennota">
      <p>Je bent reeds geregistreerd, maar de kostennota is nog niet betaald. Betaal deze nu om je inschrijving te vervolledigen.</p>
      <Button v-if="openInvoiceStatus === -99" @click="payInvoice">Betaal kostennota</Button>
      <Button v-else @click="openInvoice">Bekijk kostennota</Button>
    </Alert>
    <Availability v-if="!isBlocked && !isInvited" :simple="!showOccupation" :course="course" :tariff="tariff" />
    <Pill v-else-if="isInvited && course.disable_waiting_list && availability <= 0" danger>{{ $t('Volzet') }}</Pill>
    <template v-if="!isBlocked" #aside>
      <div>
        <div v-if="['full', 'both'].includes(priceDisplay) || !payInParts" style="text-align: right;">
          <span v-if="tariff.discountedPrice > 0">{{ tariff.discountedPrice|money }} <small class="old-price">{{ tariff.price | money }}</small><span v-if="course.isCoupleCourse">/p.p.</span></span>
          <span v-else>{{ tariff.price|money }}<span v-if="course.isCoupleCourse || parseInt(course.usersSimultaneous)">/p.p.</span></span>
        </div>
        <div v-if="['partial', 'both'].includes(priceDisplay) && payInParts && tariff.parts" class="text-success" style="text-align: right; white-space: nowrap;">
          <span v-if="tariff.parts.deposit">Voorschot: {{ tariff.parts.deposit|money }}, </span>{{ tariff.parts.pricePerMonth|money }} <small class="old-price" v-if="tariff.parts.pricePerMonth !== tariff.parts.priceWithoutDiscount">{{ tariff.parts.priceWithoutDiscount|money }}</small> p/m
        </div>
      </div>
    </template>
    <template #label>
      <Pill v-if="isInvited" info small>{{ $t('Uitgenodigd') }}</Pill>
      <Pill v-if="isAlreadyRegistered" success small>{{ $t('Reeds geregistreerd') }}</Pill>
      <Pill v-if="isQueued && !tariff.disable_waiting_list" danger small>{{ $t('Op de wachtlijst') }}</Pill>
      <Pill v-if="isInCart" info small>{{ $t('In winkelmandje') }}</Pill>
      <Pill v-if="hasOverlap" warning small>{{ $t('Overlappend tarief') }}</Pill>
    </template>
  </Card>
</template>

<script>
import Card from '../Core/Card'
import Availability from '../Tariff/Availability'
import { UserError } from '../../errors'
import Pill from '../Core/Pill'
import { canRegister, isAlreadyRegistered, isQueued, hasOverlap, isBlocked, openInvoice, payInvoice } from '../../utils'
import Modal from '../Core/Modal'
import Table from '../Table/Index'
import Alert from '../Core/Alert'
import Button from '../Core/Button'
export default {
  name: 'Tariff',
  components: {
    Button,
    Alert,
    Table,
    Modal,
    Pill,
    Availability,
    Card
  },
  props: {
    tariff: Object,
    value: [Object, Boolean],
    course: Object,
    activeFamilyMembers: Object,
    dontAllowApproval: Boolean
  },
  mounted () {
    if (!this.isAlreadyRegistered && this.tariff.blockedRegistrationType === 'requiredCourses') {
      const showRequiredCoursesKey = 'showRequiredCourses' + this.tariff.nid;
      document.getElementById(showRequiredCoursesKey).addEventListener('click', this.showRequiredCourses)
    }
  },
  computed: {
    isLoading () {
      return this.$store.getters.isLoading
    },
    cart () {
      return this.$store.getters['cart/cart']
    },
    user () {
      return this.$store.getters['user/user']
    },
    selectedUser () {
      return this.$store.getters['user/selectedUser']
    },
    isAlreadyRegistered () {
      return !parseInt(this.course.isTicketSalesEvent) && isAlreadyRegistered(this.selectedUser, this.tariff)
    },
    openInvoiceStatus () {
      return (this.isAlreadyRegistered || parseInt(this.course.isTicketSalesEvent)) && parseInt(this.tariff.registeredUsersPerTariff[this.selectedUser.uid]?.status)
    },
    hasOpenInvoice () {
      return (this.isAlreadyRegistered || parseInt(this.course.isTicketSalesEvent)) && [1, -99].includes(this.openInvoiceStatus)
    },
    isQueued () {
      return isQueued(this.selectedUser, this.tariff)
    },
    isInvited () {
      if (!this.user) return false
      if (!this.selectedUser) return false
      return !this.isAlreadyRegistered && (this.tariff.invites[this.selectedUser.uid] || (this.tariff.coupleCourseInvites && this.tariff.coupleCourseInvites[this.user.uid]))
    },
    isBlocked () {
      return isBlocked(this.tariff)
    },
    canRegister () {
      return canRegister(this.selectedUser, this.tariff, this.course) && !this.isInCart && !this.hasOverlap && !this.dontAllowApproval &&  this.course.status !== 4
    },
    availability () {
      return Math.max(0, this.tariff.availability)
    },
    club () {
      return this.$store.getters['club/club']
    },
    otherTariffs () {
      return this.course.tariffs.filter(tariff => {
        return parseInt(tariff.nid) !== parseInt(this.tariff.nid)
      }).map(tariff => {
        return {
          ...tariff,
          course: this.course
        }
      })
    },
    priceDisplay () {
      if (this.course.parts && this.course.parts.partsOnly) {
        return 'partial'
      }
      return this.club.public_po_parts_detail_price
    },
    payInParts () {
      if (!this.course.parts) return false
      if (this.tariff.price < this.course.parts.startingFromAmount) return false
      if (this.course.parts.minAmount && this.tariff.price < this.course.parts.minAmount) return false
      return this.course.parts
    },
    showOccupation () {
      if (!this.$store.getters['club/showOccupation']) {
        return false
      }
      if (this.$store.getters['club/showOccupationFrom']) {
        return this.tariff.availability <= this.$store.getters['club/showOccupationFrom']
      }
      return this.$store.getters['club/showOccupation']
    },
    otherRegisteredTariffs () {
      if (!this.selectedUser) return []
      return this.otherTariffs.filter(tariff => {
        return tariff.registeredUsersPerTariff[this.selectedUser.uid]
      })
    },
    hasOverlap () {
      return hasOverlap({ ...this.tariff, course: this.course }, [...this.$store.state.cart.tariffs, ...this.otherRegisteredTariffs])
    },
    isInCart () {
      const cart = this.$store.getters['cart/cart']
      const items = cart.items
      return items.find(item => {
        return parseInt(item.nid) === parseInt(this.tariff.nid) && item.tariff
      })
    },
    isActiveMember () {
      if (!this.selectedUser) return false
      if (!this.activeFamilyMembers || (this.activeFamilyMembers && !Object.values(this.activeFamilyMembers).length)) return false
      return this.activeFamilyMembers[this.selectedUser.uid]
    }
  },
  methods: {
    openInvoice () {
      openInvoice(this.tariff.registeredUsersPerTariff[this.selectedUser.uid].oid, this.selectedUser.uid, this.club.nid)
    },
    payInvoice () {
      payInvoice(this.tariff.registeredUsersPerTariff[this.selectedUser.uid].oid)
    },
    selectTariff () {
      if (!this.selectedUser) {
        this.$router.push({ name: 'Login', query: { redirect: this.$route.path + '/' + this.tariff.nid } })
      }
      if (this.canRegister) {
        this.$emit('input', this.tariff)
      } else {
        if (this.isAlreadyRegistered) {
          throw new UserError(this.$t('Reeds geregistreerd'), this.$t('Je bent reeds geregistreerd op dit tarief.'))
        }
        if (this.isQueued) {
          throw new UserError(this.$t('Wachtlijst'), this.$t('Je staat reeds op de wachtlijst.'))
        }
        if (this.isInCart) {
          throw new UserError(this.$t('Reeds in winkelmand'), this.$t('Je kan een tarief slechts één maal toevoegen in je winkelmandje.'))
        }
        if (this.hasOverlap) {
          throw new UserError(this.$t('Overlappend tarief'), this.$t('Je bent reeds geregistreerd voor een overlappend tarief of er zit reeds een overlappend tarief in je winkelmand.'))
        }
      }
    },
    showRequiredCourses () {
      this.$refs.requiredCoursesModal.toggle()
    }
  },
  watch: {
    selectedUser () {
      if (!this.canRegister && this.value === this.tariff) {
        this.$emit('input', null)
      }
    },
    tariff () {
      if (!this.isAlreadyRegistered && this.tariff.blockedRegistrationType === 'requiredCourses') {
        const showRequiredCoursesKey = 'showRequiredCourses' + this.tariff.nid;
        setTimeout(() => {
          document.getElementById(showRequiredCoursesKey).addEventListener('click', this.showRequiredCourses)
        }, 200)
      }

      if (!this.canRegister && this.$route.params.tariff && parseInt(this.$route.params.tariff) === parseInt(this.tariff.nid)) {
        if (this.isAlreadyRegistered) {
          throw new UserError(this.$t('Reeds geregistreerd'), this.$t('Je bent reeds geregistreerd op dit tarief.'))
        }
        if (this.isQueued) {
          throw new UserError(this.$t('Wachtlijst'), this.$t('Je staat reeds op de wachtlijst.'))
        }
        if (this.isInCart) {
          throw new UserError(this.$t('Reeds in winkelmand'), this.$t('Je kan een tarief slechts één maal toevoegen in je winkelmandje.'))
        }
        if (this.hasOverlap) {
          throw new UserError(this.$t('Overlappend tarief'), this.$t('Je bent reeds geregistreerd voor een overlappend tarief of er zit reeds een overlappend tarief in je winkelmand.'))
        }
      }
    }
  }
}
</script>
